// 発見レイヤー（v0.8.2）。まだ出会っていない客の「手がかり」を少しずつ開示する。
//
// ねらい（ユーザー要望）:
//  - 図鑑の未発見客に、どんな庭を作れば来るかのヒントを段階的に見せる。
//  - **キャラクターの相関グラフ**を持ち、関係の深い客を既に発見しているほどヒントが進む
//    （＝発見済みの常連が、仲のいい客のことを教えてくれる）。
//  - これで「どういう島・庭を作れば来るようになるか」が、遊ぶほど・出会うほど分かっていく。
//
// 描画・DOM・乱数に触れない純データ＋純関数。状態は Dex（発見状況）からの導出だけ。

import type { Dex } from './state';
import { CUSTOMER_CATALOG } from './customers';

/**
 * キャラクター相関グラフ（無向・重み付き）。重みが大きいほど「関係が深い」＝その客を
 * 発見しているとき、相手のヒントが進みやすい。モコモケ（社交的な常連）は全員と薄く繋がり、
 * 夜の住人どうし・水と結晶・冬の静寂など、ロアの近い組は強めに結ぶ。
 *
 * edges は [a, b, weight] の片側だけ書き、relationsOf が対称に展開する。
 */
const RELATION_EDGES: readonly [string, string, number][] = [
  // モコモケ（常連・社交的）は全員を少しだけ知っている。
  ['mokomoke', 'lunamoth', 1],
  ['mokomoke', 'paddle', 1],
  ['mokomoke', 'yumemi', 1],
  ['mokomoke', 'prism', 1],
  ['mokomoke', 'nemu', 1],
  // 夜の住人どうし。
  ['lunamoth', 'yumemi', 2],
  ['lunamoth', 'prism', 2],
  ['yumemi', 'prism', 2],
  // 水と結晶（プリズム獣は水鏡を要する）。
  ['paddle', 'prism', 2],
  // 昼の自然・落ち着き。
  ['paddle', 'nemu', 1],
  ['yumemi', 'nemu', 1],
  // 珍しい者どうし（古木の精と結晶の獣）。
  ['nemu', 'prism', 1],
];

/** id → 関係する客 [{id, weight}] の対称隣接表（一度だけ構築）。 */
const RELATIONS: ReadonlyMap<string, { id: string; weight: number }[]> = (() => {
  const m = new Map<string, { id: string; weight: number }[]>();
  for (const c of CUSTOMER_CATALOG) m.set(c.id, []);
  for (const [a, b, w] of RELATION_EDGES) {
    m.get(a)?.push({ id: b, weight: w });
    m.get(b)?.push({ id: a, weight: w });
  }
  return m;
})();

/** ある客の関係先（重み付き、発見状況に依らない全リスト）。グラフ表示に使う。 */
export function relationsOf(customerId: string): { id: string; weight: number }[] {
  return RELATIONS.get(customerId) ?? [];
}

/** その客が発見済みか。 */
function isDiscovered(dex: Dex, id: string): boolean {
  return !!dex[id]?.discovered;
}

/**
 * 未発見客 X について、発見済みの関係先から集めた「手がかりの強さ」。
 * 関係先のうち発見済みのものの重みを合計する（仲のいい客を知っているほど大きい）。
 */
export function relationStrength(customerId: string, dex: Dex): number {
  let sum = 0;
  for (const r of relationsOf(customerId)) {
    if (isDiscovered(dex, r.id)) sum += r.weight;
  }
  return sum;
}

/** 手がかりの強さ → 開示するヒント数の閾値（≥1で1つ、≥2で2つ、≥4で3つ）。 */
const HINT_THRESHOLDS: readonly number[] = [1, 2, 4];

/**
 * 未発見客に開示するヒント数（0..ヒント総数）。発見済みの関係先の重み合計で段階的に増える。
 * モコモケ（全員と弱く繋がる）を早期に発見するので、たいてい最初の 1 つは早く出る。
 */
export function revealedHintCount(customerId: string, dex: Dex): number {
  const hints = CUSTOMER_HINTS[customerId] ?? [];
  if (hints.length === 0) return 0;
  const strength = relationStrength(customerId, dex);
  let n = 0;
  for (const t of HINT_THRESHOLDS) if (strength >= t) n++;
  return Math.min(n, hints.length);
}

/** 未発見客に今見せるヒント（開示済みのぶんだけ）。発見済みなら空（ヒント不要）。 */
export function revealedHints(customerId: string, dex: Dex): string[] {
  if (isDiscovered(dex, customerId)) return [];
  const hints = CUSTOMER_HINTS[customerId] ?? [];
  return hints.slice(0, revealedHintCount(customerId, dex));
}

/** その未発見客について「教えてくれた」発見済みの関係先（attribution 表示用、重み降順）。 */
export function informants(customerId: string, dex: Dex): string[] {
  return relationsOf(customerId)
    .filter((r) => isDiscovered(dex, r.id))
    .sort((a, b) => b.weight - a.weight)
    .map((r) => r.id);
}

/**
 * 客ごとの段階ヒント（appears 条件をだんだん明かす）。
 * 0=気配（ぼんやり）/ 1=好み（部分的）/ 2=条件（どんな庭を作れば来るか明確に）。
 */
export const CUSTOMER_HINTS: Record<string, string[]> = {
  mokomoke: [
    'ふわふわした常連が、庭の匂いにつられて来るらしい。',
    '香りのある庭を好むみたい。',
    '花壇やキノコで香りを漂わせると、すぐに通ってくれる（いつでも来る基本の客）。',
  ],
  lunamoth: [
    '夜になると、灯りのまわりを小さな影がよぎることがあるという…',
    '光を集めた庭を好むらしい。',
    '灯りをいくつも灯して光を強く（光≥3）、にぎやかにしすぎない庭に羽を休めに来る。',
  ],
  paddle: [
    '水の匂いを辿って旅する者がいるという噂。',
    '水辺のある庭を探しているらしい。',
    '池や噴水で水辺（水辺≥2）を作ると、ふちでぷかぷか涼みに来る。',
  ],
  yumemi: [
    'とても静かな夜にだけ現れる気配があるとか…',
    '静けさを何より好むらしい。',
    'ベンチや池で静寂を深く（静寂≥4）、にぎやかをほぼ無くした庭でうとうと長居していく。',
  ],
  prism: [
    '結晶のきらめきに惹かれる珍しい獣がいるという。',
    'たくさんの結晶と、水鏡のある庭にだけ降りるらしい。',
    'クリスタルをふんだんに（結晶≥5）＋水辺を少し（水辺≥1）揃えた庭に、光を屈折させて現れる。',
  ],
  nemu: [
    '年を経た木に宿る、まどろみ好きの精がいるという噂…',
    '古い木と、緑ゆたかな庭を好むらしい。',
    '若木をじっくり育てて古木にし（古木≥1）、緑の深い（緑≥4）庭に根を下ろしに来る。',
  ],
};
